import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Lock, Wrench, Search, AlertTriangle } from "lucide-react";

function EntitlementCell({ ent }) {
  const jid = ent?.jidnyasa; const neu = ent?.neuroeq;
  return (
    <div className="flex flex-wrap gap-1">
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-widest ${
        jid ? "bg-[hsl(var(--neu-green))]/15 text-[hsl(var(--neu-green))]"
             : "bg-black/5 text-muted-foreground"
      }`}>{jid ? "✓ Jidnyasa" : "— Jidnyasa"}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-widest ${
        neu ? "bg-[hsl(var(--neu-green))]/15 text-[hsl(var(--neu-green))]"
             : "bg-black/5 text-muted-foreground"
      }`}>{neu ? "✓ NeuroEQ" : "— NeuroEQ"}</span>
    </div>
  );
}

function SubscriptionRepairCard() {
  const [email, setEmail] = useState("");
  const [scope, setScope] = useState("one"); // "one" | "all"
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {scanned, mismatched, changes}
  const [subs, setSubs] = useState(null);       // inspect results

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN",
    { day: "numeric", month: "short", year: "numeric" }) : "—";

  const inspect = async () => {
    if (!email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true); setSubs(null); setPreview(null);
    try {
      const r = await api.get(`/admin/support/subs/inspect?email=${encodeURIComponent(email.trim().toLowerCase())}`);
      setSubs(r.data);
      if (r.data.mismatched_count === 0) toast.success("All rows look correct");
      else toast.warning(`${r.data.mismatched_count} mismatched row(s) found`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not inspect");
    } finally { setBusy(false); }
  };

  const repair = async (dryRun) => {
    if (scope === "one" && !email.trim()) { toast.error("Enter an email"); return; }
    if (scope === "all" && !dryRun) {
      if (!window.confirm("Repair EVERY mismatched subscription across ALL families? This writes to the DB.")) return;
    } else if (!dryRun) {
      if (!window.confirm(`Fix subscriptions for ${email}? This writes valid_until to match the plan interval.`)) return;
    }
    setBusy(true); setPreview(null);
    try {
      const body = { scope, dry_run: dryRun };
      if (scope === "one") body.email = email.trim().toLowerCase();
      const r = await api.post("/admin/support/subs/repair", body);
      setPreview(r.data);
      if (dryRun) toast.info(`Dry run · ${r.data.mismatched} row(s) would be fixed`);
      else toast.success(`Fixed ${r.data.mismatched} row(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Repair failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-elevated p-5 mb-6" data-testid="admin-repair-card">
      <div className="flex items-start gap-3">
        <Wrench className="w-5 h-5 text-[hsl(var(--neu-orange))] mt-1"/>
        <div className="flex-1">
          <div className="font-display text-xl">Support · Fix subscription end-dates</div>
          <p className="text-xs text-muted-foreground mt-1">
            For rows where <code>valid_until</code> doesn&apos;t match the plan interval (e.g. a monthly plan expiring in 17 days instead of 30).
            Always run <b>Inspect</b> or <b>Dry run</b> first — <b>Apply repair</b> writes to the DB.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[2fr_120px] gap-3 mt-4">
        <input className="field text-sm"
                placeholder="parent@example.com (leave blank + choose scope=all to scan everyone)"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-repair-email"/>
        <select className="field text-sm" value={scope}
                 onChange={(e) => setScope(e.target.value)}
                 data-testid="admin-repair-scope">
          <option value="one">One family</option>
          <option value="all">All families</option>
        </select>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={inspect} disabled={busy || scope === "all"}
                 className="btn-ghost text-sm px-4 py-1.5"
                 data-testid="admin-repair-inspect">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Search className="w-3.5 h-3.5"/>}
          Inspect
        </button>
        <button onClick={() => repair(true)} disabled={busy}
                 className="btn-ghost text-sm px-4 py-1.5"
                 data-testid="admin-repair-dryrun">
          Dry run
        </button>
        <button onClick={() => repair(false)} disabled={busy}
                 className="btn-primary text-sm px-4 py-1.5"
                 data-testid="admin-repair-apply">
          Apply repair
        </button>
      </div>

      {/* Inspect result */}
      {subs && (
        <div className="mt-4 rounded-2xl bg-[hsl(var(--neu-paper))] p-4 text-sm">
          <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
            <AlertTriangle className={`w-3.5 h-3.5 ${subs.mismatched_count > 0 ? "text-destructive" : "text-[hsl(var(--neu-green))]"}`}/>
            {subs.subscriptions.length} subscription(s) · <b className={subs.mismatched_count > 0 ? "text-destructive" : ""}>{subs.mismatched_count} mismatched</b>
          </div>
          <div className="space-y-2">
            {subs.subscriptions.map((s) => (
              <div key={s.id} className={`text-xs p-2 rounded-xl bg-white border ${
                s.analysis.mismatch ? "border-destructive/30" : "border-black/5"
              }`}>
                <b>{s.product}</b> · {s.interval} · from {fmt(s.valid_from)} → {fmt(s.valid_until)}
                <span className="text-muted-foreground ml-2">
                  ({s.analysis.actual_days ?? "?"}d actual vs {s.analysis.expected_days ?? "?"}d expected)
                </span>
                {s.analysis.mismatch && (
                  <span className="text-destructive ml-2">→ should end {fmt(s.analysis.expected_until)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Repair result */}
      {preview && (
        <div className="mt-4 rounded-2xl bg-[hsl(var(--neu-paper))] p-4 text-sm">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {preview.dry_run ? "Dry run" : "Applied"} · scanned {preview.scanned} · <b>{preview.mismatched}</b> would-fix
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {preview.changes.map((c) => (
              <div key={c.sub_id} className="text-xs p-2 rounded-xl bg-white border border-black/5">
                <b>{c.product}</b> · {c.interval} · family <code>{c.family_id.slice(0, 8)}…</code>
                <div className="text-muted-foreground mt-0.5">
                  {fmt(c.valid_from)} → <s>{fmt(c.old_valid_until)}</s> → <b className="text-[hsl(var(--neu-green))]">{fmt(c.new_valid_until)}</b>
                  <span className="ml-2">(Δ {c.delta_days > 0 ? "+" : ""}{c.delta_days}d) {c.applied ? "✓ applied" : preview.dry_run ? "· planned" : "· not applied"}</span>
                </div>
              </div>
            ))}
            {preview.mismatched === 0 && (
              <div className="text-center text-muted-foreground py-2">No rows need fixing 🎉</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GrantAccessCard({ onGranted }) {
  const [email, setEmail] = useState("");
  const [product, setProduct] = useState("jidnyasa");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const grant = async () => {
    if (!email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      await api.post("/admin/support/grant-access",
                      { email: email.trim().toLowerCase(), product, days });
      toast.success(`Granted ${product} for ${days} days to ${email}`);
      setEmail("");
      onGranted && onGranted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not grant access");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-elevated p-5 mb-6" data-testid="admin-grant-card">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-[hsl(var(--neu-orange))] mt-1"/>
        <div className="flex-1">
          <div className="font-display text-xl">Support · Grant access</div>
          <p className="text-xs text-muted-foreground mt-1">
            For customers whose payment succeeded but their access didn&apos;t unlock. Creates a manual subscription record.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_100px_120px] gap-3 mt-4">
        <input className="field text-sm" placeholder="parent@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-grant-email"/>
        <select className="field text-sm" value={product}
                 onChange={(e) => setProduct(e.target.value)}
                 data-testid="admin-grant-product">
          <option value="jidnyasa">Jidnyasa</option>
          <option value="neuroeq">NeuroEQ</option>
          <option value="both">Both</option>
        </select>
        <input type="number" className="field text-sm" value={days} min="1" max="365"
                onChange={(e) => setDays(parseInt(e.target.value || "30", 10))}
                data-testid="admin-grant-days"/>
        <button onClick={grant} disabled={busy} className="btn-primary text-sm"
                data-testid="admin-grant-btn">
          {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : "Grant"}
        </button>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-users-screen">
      <h1 className="font-display text-4xl mb-2">Users</h1>
      <p className="text-muted-foreground mb-6">All families with per-product entitlements.</p>

      <GrantAccessCard onGranted={load}/>
      <SubscriptionRepairCard/>

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[hsl(var(--neu-paper))] text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Parent</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Child</th>
                <th className="text-left p-3">Entitlements</th>
                <th className="text-right p-3">Sessions</th>
                <th className="text-right p-3">Streak</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u, i) => (
                <tr key={i} className="border-t"
                     data-testid={`admin-user-row-${i}`}>
                  <td className="p-3">{u.parent?.name}</td>
                  <td className="p-3 text-muted-foreground">{u.user?.email}</td>
                  <td className="p-3">{u.child?.name || "-"} <span className="text-muted-foreground">({u.child?.age_group || "-"})</span></td>
                  <td className="p-3"><EntitlementCell ent={u.entitlements}/></td>
                  <td className="p-3 text-right">{u.completed_sessions}</td>
                  <td className="p-3 text-right">{u.current_streak}</td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No families yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
